import React from 'react'

/**
 * Shared shimmer base. Animated gradient sweeping across a dark surface.
 * Width set to 200% so the keyframe slides the highlight across the visible area.
 */
const shimmerStyle: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(90deg, rgba(26,26,26,0) 0%, rgba(48,197,210,0.08) 45%, rgba(139,92,246,0.10) 55%, rgba(26,26,26,0) 100%)',
  backgroundSize: '200% 100%',
  backgroundColor: '#1A1A1A',
}

type SkeletonBlockProps = {
  className?: string
  style?: React.CSSProperties
}

export const SkeletonBlock: React.FC<SkeletonBlockProps> = ({ className = '', style }) => (
  <div
    className={`rounded-md animate-shimmer ${className}`}
    style={{ ...shimmerStyle, ...style }}
  />
)

/* ──────────────────────────────────────────────────────────────────────────
 * TableSkeleton
 * Shimmer rows shaped like the table that's loading. Renders as either
 * <tr>s (desktop, inside an existing <tbody>) or stacked cards (mobile).
 * Choose the mode via the `mode` prop.
 * ──────────────────────────────────────────────────────────────────────── */
type TableSkeletonProps = {
  rows?: number
  columns?: number
  mode?: 'table' | 'cards'
  /** Width of an optional leading "row number" column. Omit to skip. */
  showRowNumberColumn?: boolean
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 8,
  columns = 5,
  mode = 'table',
  showRowNumberColumn = false,
}) => {
  if (mode === 'cards') {
    return (
      <div className="divide-y divide-dark-border/50">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <SkeletonBlock className="h-4 w-1/3" />
              <SkeletonBlock className="h-4 w-16" />
            </div>
            <SkeletonBlock className="h-3 w-2/3" />
            <SkeletonBlock className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    )
  }

  const colCount = showRowNumberColumn ? columns + 1 : columns
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr
          key={r}
          className="border-b border-dark-border/40"
          style={{ animationDelay: `${r * 40}ms` }}
        >
          {Array.from({ length: colCount }).map((__, c) => {
            // Vary widths so it doesn't look like a wall of identical bars
            const widths = ['85%', '60%', '70%', '45%', '55%', '75%', '40%', '65%']
            const w = c === 0 && showRowNumberColumn ? '40%' : widths[c % widths.length]
            return (
              <td key={c} className="px-6 py-3">
                <SkeletonBlock className="h-4" style={{ width: w }} />
              </td>
            )
          })}
        </tr>
      ))}
    </>
  )
}

/* ──────────────────────────────────────────────────────────────────────────
 * InlineLoader
 * Small loader for inside cards/panels/sections. Two variants:
 *   <InlineLoader />            → shimmer block(s), best for content panels
 *   <InlineLoader variant="dot" message="Refreshing" /> → dot + label, best
 *                                                          for header strips
 * ──────────────────────────────────────────────────────────────────────── */
type InlineLoaderProps = {
  message?: string
  lines?: number
  variant?: 'skeleton' | 'dot'
  className?: string
}

export const InlineLoader: React.FC<InlineLoaderProps> = ({
  message,
  lines = 3,
  variant = 'skeleton',
  className = '',
}) => {
  if (variant === 'dot') {
    return (
      <div className={`flex items-center gap-2 text-xs text-gray-400 ${className}`}>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-teal opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-teal" />
        </span>
        {message && <span className="tracking-wide">{message}…</span>}
      </div>
    )
  }

  // Skeleton variant
  const widths = ['100%', '85%', '70%', '60%', '90%']
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {message && (
        <div className="text-xs text-gray-500 tracking-wide uppercase">{message}…</div>
      )}
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock key={i} className="h-4" style={{ width: widths[i % widths.length] }} />
      ))}
    </div>
  )
}

const Loader = {
  TableSkeleton,
  InlineLoader,
  SkeletonBlock,
}

export default Loader
